import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { useTranslation } from 'react-i18next';
import BrushIcon from '../../../assets/svg/brush.svg?react';
import { useAppSelector } from '../hooks/useAppSelector';
import { selectBusinessProfileStatus } from '../../slices/business-profile/businessProfileSlice';
import ConfirmModal from '../confirm-modal/ConfirmModal';
import styles from './SupplierDetailsCard.module.scss';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { Supplier } from '../../models/supplier.model';
import RoomPreferencesOutlinedIcon from '@mui/icons-material/RoomPreferencesOutlined';
import { AccountCircleOutlined } from '@mui/icons-material';
import { updateSupplier } from '../../slices/suppliers/suppliersSlice';
import { useCheckPermission } from '../hooks/useCheckPermission';
import { USER_PERMISSIONS } from '../constants/permissions';

interface CustomerDetailsCardProps {
  simplified?: boolean;
  preferences?: boolean;
  supplier: Supplier;
}

export default function CustomerDetailsCard({
  simplified,
  preferences,
  supplier,
}: CustomerDetailsCardProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [isEdited, setIsEdited] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const formRef = useRef<HTMLDivElement | null>(null);
  const [popperNode] = useState<HTMLDivElement | null>(null);

  const businessProfileStatus = useAppSelector(selectBusinessProfileStatus);

  const isLoading = businessProfileStatus === 'loading';

  const toggleEditing = () => {
    setIsEdited((prev) => !prev);
  };

  const checkPermission = useCheckPermission();

  const [formData, setFormData] = useState<Partial<Supplier>>({
    id: supplier?.id,
    name: supplier?.name,
    registration_number: supplier?.registration_number,
    website: supplier?.website,
    physical_address: supplier?.physical_address,
    postal_address: supplier?.postal_address,
    industry: supplier?.industry,
    description: supplier?.description,
    supplier_type: supplier?.supplier_type,
  });

  const cancelChanges = () => {
    setFormData({
      id: supplier?.id,
      name: supplier?.name,
      registration_number: supplier?.registration_number,
      website: supplier?.website,
      physical_address: supplier?.physical_address,
      postal_address: supplier?.postal_address,
      industry: supplier?.industry,
      description: supplier?.description,
      supplier_type: supplier?.supplier_type,
    });
    setIsEdited(false);
  };

  const saveChanges = () => {
    if (!supplier?.id) return;

    const payload = {
      name: formData.name,

      registration_number: formData.registration_number,
      website: formData.website,

      physical_address: formData.physical_address,
      postal_address: formData.postal_address,
      industry: formData.industry,
      description: formData.description,
      supplier_type: formData.supplier_type,
    };

    dispatch(
      updateSupplier({
        id: supplier.id,
        company: supplier.company,
        ...payload,
      })
    );
    setIsEdited(false);
    setShowModal(false);
  };

  const confirmExit = () => {
    setIsEdited(false);
    setShowModal(false);
  };

  const handleChange =
    <K extends keyof Supplier>(field: K) =>
    (value: Supplier[K]) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isEdited &&
        formRef.current &&
        !formRef.current.contains(event.target as Node) &&
        (!popperNode || !popperNode.contains(event.target as Node))
      ) {
        const targetElement = event.target as HTMLElement;
        if (event.target instanceof HTMLElement) {
          // if (ignoredElements.includes(event.target.tagName)) return;
        }

        if (
          targetElement.closest('.MuiMenuItem-root') || // Ignore clicks on MenuItem
          targetElement.closest('.MuiSelect-root') // Ignore clicks on Select
        ) {
          return;
        }
        setTimeout(() => {
          setShowModal(true);
        }, 0);
      }
    };

    if (isEdited) document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEdited, popperNode]);

  const supplierTypeOptions = [
    'Business Partner',
    'Client',
    'Consultant',
    'Contractor',
    'Manufacturer',
    'Supplier of goods and services',
  ];

  return (
    <Grid container className='inputs-editable'>
      <Grid size={{ xs: 12 }}>
        <Card
          className={`${styles.card} ${isEdited ? styles.card_border : ''} ${
            preferences && styles.preferences_card
          }`}
          ref={formRef}
        >
          <CardContent
            className={isLoading ? styles.card_content_min_height : ''}
            sx={{ paddingTop: 0, paddingBottom: '0 !important' }}
          >
            <Grid
              container
              alignItems='center'
              justifyContent='space-between'
              paddingTop='16px !important'
              paddingLeft='0 !important'
              paddingRight='0 !important'
              paddingBottom='16px !important'
              minHeight='76px'
              flexWrap='nowrap'
              p={2}
            >
              {!preferences ? (
                <Grid container alignItems='center' gap={1}>
                  <Grid sx={{ marginBottom: '0.2rem' }}>
                    <AccountCircleOutlined />
                  </Grid>
                  <Grid>
                    <Typography variant='h6' className='semibold-variant'>
                      {t('labels.customerDetails')}
                    </Typography>
                  </Grid>
                </Grid>
              ) : (
                <Grid container alignItems='center' gap={1}>
                  <Grid sx={{ marginBottom: '0.2rem' }}>
                    <RoomPreferencesOutlinedIcon />
                  </Grid>
                  <Grid>
                    <Typography variant='h6' className='semibold-variant'>
                      {t('labels.supplierIndustry')}
                    </Typography>
                  </Grid>
                </Grid>
              )}
              <Grid size={{ xs: 1 }} container justifyContent='flex-end'>
                {!isEdited ? (
                  <Tooltip
                    className='flex-shrink-0'
                    title={
                      checkPermission(USER_PERMISSIONS.ORG_SUPPLIER_PROFILE_UPDATE)
                        ? t('buttons.edit')
                        : t('messages.contactAdministrator')
                    }
                    placement='top'
                  >
                    <IconButton
                      onClick={toggleEditing}
                      disabled={!checkPermission(USER_PERMISSIONS.ORG_SUPPLIER_PROFILE_UPDATE)}
                    >
                      <BrushIcon />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Box display='flex' gap='8px' alignItems='center'>
                    <Button
                      type='button'
                      className='btn-default btn-action-basic btn-cancel'
                      role='button'
                      variant='contained'
                      size='large'
                      onClick={cancelChanges}
                    >
                      {t('buttons.cancel')}
                    </Button>
                    <Button
                      type='submit'
                      className='btn-default btn-action-basic'
                      role='button'
                      disabled={!isEdited}
                      variant='contained'
                      size='large'
                      onClick={saveChanges}
                    >
                      {t('buttons.save')}
                    </Button>
                  </Box>
                )}
              </Grid>
            </Grid>

            {!simplified ? (
              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  {' '}
                  <TextField
                    id='supplier-name'
                    label={t('labels.supplierName')}
                    variant='outlined'
                    fullWidth
                    value={formData.name ?? ''}
                    onChange={(e) => handleChange('name')(e.target.value)}
                    disabled={!isEdited}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  {' '}
                  <TextField
                    id='registration-number'
                    label={t('labels.supplierRegistrationNumber')}
                    variant='outlined'
                    fullWidth
                    value={formData.registration_number ?? ''}
                    onChange={(e) => handleChange('registration_number')(e.target.value)}
                    disabled={!isEdited}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  {' '}
                  <TextField
                    id='supplierEmail'
                    label={t('labels.supplierEmail')}
                    variant='outlined'
                    fullWidth
                    value={formData.supplier_email ?? ''}
                    onChange={(e) => handleChange('supplier_email')(e.target.value)}
                    disabled={!isEdited}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  {' '}
                  <TextField
                    id='website'
                    label={t('labels.website')}
                    variant='outlined'
                    fullWidth
                    value={formData.website ?? ''}
                    onChange={(e) => handleChange('website')(e.target.value)}
                    disabled={!isEdited}
                  />
                </Grid>

                <Grid size={{ xs: 6 }}>
                  {' '}
                  <TextField
                    id='description'
                    label={t('labels.supplierDescription')}
                    variant='outlined'
                    fullWidth
                    value={formData.description ?? ''}
                    onChange={(e) => handleChange('description')(e.target.value)}
                    disabled={!isEdited}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  {' '}
                  <TextField
                    id='physical-address'
                    label={t('formInputs.physicalAddressInputLabel')}
                    variant='outlined'
                    fullWidth
                    value={formData.physical_address ?? ''}
                    onChange={(e) => handleChange('physical_address')(e.target.value)}
                    disabled={!isEdited}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  {' '}
                  <TextField
                    id='postal-address'
                    label={t('formInputs.postalAddressInputLabel')}
                    variant='outlined'
                    fullWidth
                    value={formData.postal_address ?? ''}
                    onChange={(e) => handleChange('postal_address')(e.target.value)}
                    disabled={!isEdited}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  {' '}
                  <FormControl fullWidth variant='outlined' disabled={!isEdited}>
                    <InputLabel htmlFor='supplierType'>{t('labels.type')}</InputLabel>
                    <Select
                      fullWidth
                      sx={{
                        '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.23) !important',
                        },
                      }}
                      value={formData.supplier_type}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleChange('supplier_type')(e.target.value);
                      }}
                      disabled={!isEdited}
                      displayEmpty
                      slotProps={{ input: { id: 'supplierType' } }}
                    >
                      <MenuItem value='' disabled>
                        Select Supplier Type
                      </MenuItem>
                      {supplierTypeOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  {' '}
                  <TextField
                    id='industry'
                    label={t('labels.industry')}
                    variant='outlined'
                    fullWidth
                    value={formData.industry ?? ''}
                    onChange={(e) => handleChange('industry')(e.target.value)}
                    disabled={!isEdited}
                  />
                </Grid>
              </Grid>
            ) : (
              simplified &&
              !preferences && (
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    {' '}
                    <TextField
                      id='supplier-name'
                      label={t('labels.supplierName')}
                      variant='outlined'
                      fullWidth
                      value={formData.name ?? ''}
                      onChange={(e) => handleChange('name')(e.target.value)}
                      disabled={!isEdited}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    {' '}
                    <TextField
                      id='registration-number'
                      label={t('labels.supplierRegistrationNumber')}
                      variant='outlined'
                      fullWidth
                      value={formData.registration_number ?? ''}
                      onChange={(e) => handleChange('registration_number')(e.target.value)}
                      disabled={!isEdited}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    {' '}
                    <TextField
                      id='website'
                      label={t('labels.website')}
                      variant='outlined'
                      fullWidth
                      value={formData.website ?? ''}
                      onChange={(e) => handleChange('website')(e.target.value)}
                      disabled={!isEdited}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    {' '}
                    <TextField
                      id='description'
                      label={t('labels.supplierDescription')}
                      variant='outlined'
                      fullWidth
                      value={formData.description ?? ''}
                      onChange={(e) => handleChange('description')(e.target.value)}
                      disabled={!isEdited}
                    />
                  </Grid>
                </Grid>
              )
            )}
            {simplified && preferences && (
              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  {' '}
                  <FormControl fullWidth variant='outlined' disabled={!isEdited}>
                    <InputLabel id='supplier-type-label'>{t('labels.type')}</InputLabel>
                    <Select
                      fullWidth
                      sx={{
                        '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.23) !important',
                        },
                      }}
                      value={formData.supplier_type}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleChange('supplier_type')(e.target.value);
                      }}
                      disabled={!isEdited}
                      displayEmpty
                      inputProps={{ 'aria-label': t('labels.type') }}
                    >
                      <MenuItem value='' disabled>
                        Select Supplier Type
                      </MenuItem>
                      {supplierTypeOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  {' '}
                  <TextField
                    id='industry'
                    label={t('labels.industry')}
                    variant='outlined'
                    fullWidth
                    value={formData.industry ?? ''}
                    onChange={(e) => handleChange('industry')(e.target.value)}
                    disabled={!isEdited}
                  />
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>
      </Grid>
      <ConfirmModal open={showModal} onClose={saveChanges} onConfirm={confirmExit} />
    </Grid>
  );
}
